import { mergeReplaceParams, nextPlanItemLabel } from "@fast-quotation/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../components/ui/button";
import { Badge, Input, Select, Separator, Table, Td, Th } from "../components/ui/misc";
import { PageRail } from "../features/drawing/PageRail";
import {
  type BoxRect,
  type CanvasView,
  MAX_SCALE,
  MIN_SCALE,
  PdfCanvas,
} from "../features/drawing/PdfCanvas";
import {
  api,
  type CatalogItemRow,
  type DrawingRow,
  type PlanItemComponentRow,
  type PlanItemRow,
  type PlanRow,
} from "../lib/api";
import { shouldFollowTypeDefault } from "../lib/cabinet-defaults";
import { readPageSizes } from "../lib/pdf";
import { ROOM_LABELS, roomOptionText } from "../lib/room-labels";
import {
  formatFeetInches,
  formatLength,
  INCH_QUICK_REFERENCE,
  isLengthUnit,
  LENGTH_UNIT_META,
  LENGTH_UNITS,
  type LengthUnit,
  MM_PER_INCH,
  parseLength,
  roundToMm,
} from "../lib/units";
import { formatMoney } from "../lib/utils";

type Detail = {
  plan: PlanRow;
  items: PlanItemRow[];
  components: PlanItemComponentRow[];
  drawings: DrawingRow[];
  children: PlanRow[];
};

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName);

/** 组件分类 → 条目里的语义槽位（不要用分类代码当 slot） */
const SLOT_BY_CATEGORY: Record<string, string> = {
  cabinet: "box",
  door: "door",
  panel: "panel",
  countertop: "countertop",
  hardware: "hardware",
  accessory: "accessory",
};
const slotForCategory = (categoryCode: string) =>
  SLOT_BY_CATEGORY[categoryCode.split(".")[0] ?? ""] ?? "extra";

/**
 * 单值槽位：一个柜体只能有一块柜体板 / 一种门板 / 一个五金方案。
 * 从这里选组件 = **换材料（替换）**，不是再加一行，否则会重复计价。
 * 台面 / 见光板 / 配件是多值槽位，可以叠加（岛台两个台面、多个拉篮等）。
 */
const SINGLE_VALUE_SLOTS = new Set(["box", "door", "hardware"]);

/** 组件行的参数（JSON 字符串）解析 */
function componentParams(component: { params: string }): Record<string, unknown> {
  try {
    return JSON.parse(component.params) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** 面积法用哪两个尺寸：展示用（让用户知道该填长/高/深里的哪两个） */
function areaBasisLabel(params: Record<string, unknown>): string {
  const from = params.areaFrom;
  if (from === "wd") return "长×深";
  if (from === "hd") return "高×深";
  return "宽×高";
}

/** 顶层分类中文名（组件选择器用） */
const CATEGORY_LABEL: Record<string, string> = {
  cabinet: "柜体",
  door: "门板",
  panel: "见光板",
  countertop: "台面",
  hardware: "五金",
  accessory: "配件",
};

/** 客户报价可选币种 + 默认汇率（1 单位该币种 = 多少 CNY 成本，可在界面里改） */
const CURRENCY_PRESETS: Array<{ code: string; label: string; rate: number }> = [
  { code: "USD", label: "美元 USD", rate: 7.2 },
  { code: "AUD", label: "澳元 AUD", rate: 4.7 },
  { code: "GBP", label: "英镑 GBP", rate: 9.1 },
  { code: "EUR", label: "欧元 EUR", rate: 7.8 },
  { code: "CNY", label: "人民币 CNY", rate: 1 },
];

/**
 * 四栏工作台（模仿老产品的全屏布局）：
 * 1 页码导航 | 2 画布 | 3 组件导航 | 4 组件详情
 */
/** 拖拽手柄（六个圆点）：提示这一行可以抓着排序，纯装饰 */
function GripIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 10 16" aria-hidden className={className} fill="currentColor">
      {[3, 8, 13].map((y) => [2.5, 7.5].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.15} />))}
    </svg>
  );
}

export function PlanWorkspace({ planId }: { planId: string }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [libraryId, setLibraryId] = useState<string | null>(null);
  const [navTab, setNavTab] = useState<"cabinets" | "library">("cabinets");
  /** 列表拖拽排序：正在拖的条目 */
  const [dragItemId, setDragItemId] = useState<string | null>(null);
  /**
   * 拖拽标记的同步副本：dragover / drop 可能紧跟在 dragstart 之后（同一帧内），
   * 这时 state 还没提交、处理器闭包里读到的还是上一轮的值，只能用 ref 读。
   */
  const dragItemIdRef = useRef<string | null>(null);
  /** 列表拖拽排序：当前落点（插到哪一条的前面 / 后面） */
  const [dropSlot, setDropSlot] = useState<{ id: string; edge: "before" | "after" } | null>(null);
  /** 拖到列表上/下边缘时的自动滚动定时器（条目多的时候不用先滚到位再拖） */
  const autoScrollRef = useRef<{ timer: number; node: HTMLElement } | null>(null);
  const dragPointRef = useRef<{ x: number; y: number } | null>(null);
  const [cabinetTypes, setCabinetTypes] = useState<
    Array<{
      code: string;
      name: string;
      default_height_mm: number | null;
      default_depth_mm: number | null;
    }>
  >([]);
  /** 批量替换面板 */
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDraft, setBulkDraft] = useState({
    scopeKind: "plan" as "plan" | "cabinet_type" | "selected",
    cabinetTypeCode: "base",
    slot: "door",
    category: "door",
    query: "",
    fromCode: "",
    catalogItemId: "",
  });
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  /** 批量弹窗的结果是成功还是失败：失败不能还用成功绿显示 */
  const [bulkFailed, setBulkFailed] = useState(false);
  /** 批量弹窗（原生 dialog，showModal 才会带 Esc / 焦点陷阱 / 背景 inert） */
  const bulkDialogRef = useRef<HTMLDialogElement | null>(null);
  /** 批量弹窗的两种用法：换材料（替换） / 加项目（纸箱、木箱、组装…） */
  const [bulkMode, setBulkMode] = useState<"replace" | "add">("replace");
  /** 批量加项：每组柜子加几个 */
  const [bulkQuantity, setBulkQuantity] = useState("1");
  const [catalog, setCatalog] = useState<CatalogItemRow[]>([]);
  const [drawingId, setDrawingId] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  /** 画布视口：滚轮 / 平移 / 工具栏按钮共用这一份状态。 */
  const [view, setView] = useState<CanvasView>({ scale: 1, x: 0, y: 0 });
  /**
   * 画框是"一次性工具"：默认选择模式；按 R / 点画框 → 触发一次；
   * 画完自动回到选择模式；勾了「连续」才保持激活（老产品的习惯）。
   */
  const [tool, setTool] = useState<"select" | "draw">("select");
  const [continuous, setContinuous] = useState(false);
  /**
   * 尺寸/数量的输入草稿。必须是**受控 + 跟着选中条目重置**：
   * 之前用 defaultValue（非受控）时，React 会复用同一个 input DOM，
   * 切到另一个柜体时框里还留着上一个柜体的数字——看起来就是"尺寸串了"。
   */
  const [dimsDraft, setDimsDraft] = useState<Record<string, string>>({});
  const [dimsForItemId, setDimsForItemId] = useState<string | null>(null);
  /** 房间名草稿：和尺寸一样受控 + 跟着选中条目重置，避免"串到上一个柜体" */
  const [roomDraft, setRoomDraft] = useState("");
  /** "添加组件"选择器：按分类 + 搜索。 */
  const [pickerCategory, setPickerCategory] = useState("countertop");
  const [pickerQuery, setPickerQuery] = useState("");
  /** 标尺被选中（右侧面板显示它的属性，可改实际长度 / 删除） */
  const [rulerSelected, setRulerSelected] = useState(false);
  /** 刚放出来的标尺：自动聚焦"实际长度"输入框，省一次点击 */
  const [rulerJustCreated, setRulerJustCreated] = useState(false);
  /** 刚用标尺画出来的条目：自动聚焦"高"输入框（平面图量不出高） */
  const [focusHeightFor, setFocusHeightFor] = useState<string | null>(null);
  const rulerLengthRef = useRef<HTMLInputElement | null>(null);
  /**
   * 尺寸输入单位。图纸是英制（英寸）时切到「英寸」，
   * 之后标尺长度、长/深/高的手填数字都按英寸写，落库前统一换算成毫米
   * （invariants §17：库里只有毫米，单位只是输入/显示的口径）。
   */
  const [inputUnit, setInputUnit] = useState<LengthUnit>("mm");
  /** 换算器弹窗：临时换算，不写任何数据 */
  const [converterOpen, setConverterOpen] = useState(false);
  const [converterDraft, setConverterDraft] = useState({ value: "24", unit: "in" as LengthUnit });
  const converterDialogRef = useRef<HTMLDialogElement | null>(null);

  /** 弹窗开关交给原生 dialog：showModal() 自带焦点陷阱、背景 inert 与 Esc 关闭 */
  useEffect(() => {
    const dialog = bulkDialogRef.current;
    if (!dialog) return;
    if (bulkOpen && !dialog.open) dialog.showModal();
    else if (!bulkOpen && dialog.open) dialog.close();
  }, [bulkOpen]);
  useEffect(() => {
    const dialog = converterDialogRef.current;
    if (!dialog) return;
    if (converterOpen && !dialog.open) dialog.showModal();
    else if (!converterOpen && dialog.open) dialog.close();
  }, [converterOpen]);

  /**
   * 输入单位按方案记住：同一份图纸的单位不会变，切页面回来还是上次选的。
   * localStorage 存不了（隐私模式）只影响"记住"，不影响换算本身。
   */
  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(`fast-quotation:input-unit:${planId}`);
    } catch {
      saved = null;
    }
    setInputUnit(isLengthUnit(saved) ? saved : "mm");
  }, [planId]);

  const changeInputUnit = (next: LengthUnit) => {
    setInputUnit(next);
    try {
      window.localStorage.setItem(`fast-quotation:input-unit:${planId}`, next);
    } catch {
      /* 隐私模式下忽略 */
    }
  };

  /** 报价设置草稿（币种 / 汇率 / 加价系数 / 整单调整 / 付款方式 / 是否整装出货） */
  const [settingsDraft, setSettingsDraft] = useState({
    currency: "USD",
    exchangeRate: "7.2",
    markupFactor: "1.25",
    globalAdjustment: "0",
    includeFloorPlan: true,
    paymentTerms: "deposit_50_50",
    assembledShipment: false,
  });
  /** 报价名内联改名：点标题进入编辑，回车保存、Esc 取消 */
  const [renamingPlan, setRenamingPlan] = useState(false);
  const [planNameDraft, setPlanNameDraft] = useState("");
  const [pages, setPages] = useState<
    Array<{
      page_index: number;
      width: number;
      height: number;
      rotation: number;
      ruler_x1: number | null;
      ruler_y1: number | null;
      ruler_x2: number | null;
      ruler_y2: number | null;
      ruler_length_mm: number | null;
    }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  /** 画完还没拿到服务端 id 的临时框（乐观渲染，避免闪烁）。 */
  const [pending, setPending] = useState<
    Array<
      {
        id: string;
        label: string;
        cabinet_type_code: string;
        size_status: string;
        /** 框属于哪张图纸：图片型图纸每张都只有第 0 页，必须连图纸一起判，否则会串页 */
        drawing_id: string | null;
        page_index: number;
      } & BoxRect
    >
  >([]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const [planDetail, types, items] = await Promise.all([
        api.plan(planId),
        api.cabinetTypes(),
        api.catalogItems(),
      ]);
      setDetail(planDetail);
      setCabinetTypes(types.cabinetTypes);
      setCatalog(items.items);
      setDrawingId((current) => current ?? planDetail.drawings[0]?.id ?? null);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "加载失败");
    }
  }, [planId]);

  useEffect(() => {
    void load();
  }, [load]);

  // 卸载/切方案时别把边缘自动滚动的定时器留在后台
  useEffect(
    () => () => {
      if (autoScrollRef.current) window.clearInterval(autoScrollRef.current.timer);
    },
    [],
  );

  // 读这一页的标尺（每页可以各自量一把）
  useEffect(() => {
    if (!drawingId) return;
    let cancelled = false;
    void api
      .drawing(drawingId)
      .then((response) => {
        if (!cancelled) setPages(response.pages);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [drawingId, detail?.drawings.length, pageIndex]);

  const currentPage = pages.find((page) => page.page_index === pageIndex) ?? null;

  // 方案加载/重算后，把报价设置草稿同步成当前值
  useEffect(() => {
    if (!detail) return;
    setSettingsDraft({
      currency: detail.plan.currency,
      exchangeRate: String(detail.plan.exchange_rate),
      markupFactor: String(detail.plan.markup_factor),
      globalAdjustment: String(detail.plan.global_adjustment_percent),
      includeFloorPlan: (detail.plan.include_floor_plan ?? 1) !== 0,
      paymentTerms: detail.plan.payment_terms ?? "deposit_50_50",
      assembledShipment: (detail.plan.assembled_shipment ?? 0) !== 0,
    });
  }, [
    detail?.plan.id,
    detail?.plan.currency,
    detail?.plan.exchange_rate,
    detail?.plan.markup_factor,
    detail?.plan.global_adjustment_percent,
    detail?.plan.include_floor_plan,
    detail?.plan.payment_terms,
    detail?.plan.assembled_shipment,
    detail,
  ]);

  // 换页时取消标尺选中（每页的标尺各自独立）
  useEffect(() => {
    setRulerSelected(false);
    setRulerJustCreated(false);
  }, [pageIndex, drawingId]);

  // 刚点「标尺」放出来时，光标直接落在工具条的长度输入框里
  useEffect(() => {
    if (!rulerJustCreated) return;
    const input = rulerLengthRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [rulerJustCreated]);

  /**
   * 点「标尺」= 立刻在画布中间放一条红线（每页一把）。
   * 之后：拖到图纸上已知尺寸的位置 → 填它的实际长度。这页已经有标尺就直接选中它。
   */
  const ensureRuler = () => {
    if (!drawingId || !currentPage) return;
    setTool("select");
    if (ruler) {
      setRulerSelected(true);
      setSelectedId(null);
      setStatus("已选中本页标尺：拖动它，或在右侧改实际长度");
      return;
    }
    void (async () => {
      // 水平放在页面正中，宽度占 30%
      const segment = { x1: 0.35, y1: 0.5, x2: 0.65, y2: 0.5 };
      await api.saveRuler(drawingId, pageIndex, { ...segment, lengthMm: 3000 });
      const refreshed = await api.drawing(drawingId);
      setPages(refreshed.pages);
      setRulerSelected(true);
      setSelectedId(null);
      setRulerJustCreated(true);
      setStatus("标尺已放在画布中间：拖到图纸上标注的尺寸线上，再填它的实际长度");
    })();
  };

  const ruler =
    currentPage &&
    currentPage.ruler_length_mm !== null &&
    currentPage.ruler_x1 !== null &&
    currentPage.ruler_y1 !== null &&
    currentPage.ruler_x2 !== null &&
    currentPage.ruler_y2 !== null
      ? {
          x1: currentPage.ruler_x1,
          y1: currentPage.ruler_y1,
          x2: currentPage.ruler_x2,
          y2: currentPage.ruler_y2,
          lengthMm: currentPage.ruler_length_mm,
        }
      : null;

  /** 图上 1 个单位（PDF pt / 图片像素）等于多少毫米；没量标尺就是 null */
  const rulerLengthOnPage = (() => {
    if (!currentPage || !ruler) return null;
    const dx = (ruler.x2 - ruler.x1) * currentPage.width;
    const dy = (ruler.y2 - ruler.y1) * currentPage.height;
    return Math.hypot(dx, dy);
  })();

  const mmPerPageUnit = (() => {
    if (!currentPage || !ruler) return null;
    const dx = (ruler.x2 - ruler.x1) * currentPage.width;
    const dy = (ruler.y2 - ruler.y1) * currentPage.height;
    const length = Math.hypot(dx, dy);
    return length > 0 ? ruler.lengthMm / length : null;
  })();

  const selected = detail?.items.find((item) => item.id === selectedId) ?? null;
  const libraryItem = catalog.find((item) => item.id === libraryId) ?? null;

  // 选中条目变了（或该条目的服务端值变了）就把草稿重置成这个条目的真实值
  useEffect(() => {
    if (!selected) {
      setDimsForItemId(null);
      setDimsDraft({});
      return;
    }
    setDimsForItemId(selected.id);
    setRoomDraft(selected.room ?? "");
    setDimsDraft({
      // 库里是毫米，输入框按当前单位显示：英寸图纸上就看得到 24 而不是 610
      width_mm: selected.width_mm === null ? "" : formatLength(selected.width_mm, inputUnit),
      height_mm: selected.height_mm === null ? "" : formatLength(selected.height_mm, inputUnit),
      depth_mm: selected.depth_mm === null ? "" : formatLength(selected.depth_mm, inputUnit),
      doors: String(selected.doors),
      drawers: String(selected.drawers),
      shelves: String(selected.shelves),
    });
  }, [
    inputUnit,
    selected?.id,
    selected?.width_mm,
    selected?.height_mm,
    selected?.depth_mm,
    selected?.doors,
    selected?.drawers,
    selected?.shelves,
    selected?.room,
    selected,
  ]);
  const activeDrawing = detail?.drawings.find((drawing) => drawing.id === drawingId) ?? null;
  const pageCount = activeDrawing?.page_count ?? 0;
  const pageItems = useMemo(
    () => [
      // 本地"创建中"的占位框：画完立刻出现，服务端返回后再换成正式条目，中间不重取整表
      ...pending.filter((item) => item.drawing_id === drawingId && item.page_index === pageIndex),
      ...(detail?.items ?? [])
        // 框的归属是「图纸 + 页码」两个维度：只按页码过滤时，两张图片（各自都是第 0 页）
        // 会互相串框——在 A 图画完切到 B 图，框还留在画布上。
        .filter((item) => item.drawing_id === drawingId && item.page_index === pageIndex)
        .map((item) => ({
          id: item.id,
          label: item.label,
          cabinet_type_code: item.cabinet_type_code,
          size_status: item.size_status,
          box_x: item.box_x,
          box_y: item.box_y,
          box_w: item.box_w,
          box_h: item.box_h,
          rotation_deg: item.rotation_deg ?? 0,
        })),
    ],
    [detail, drawingId, pageIndex, pending],
  );
  const components = (detail?.components ?? []).filter((component) => component.item_id === selectedId);
  /** 柜型默认高/深：平面图量不出高，画框时按它先填一个可改的值 */
  const defaultHeightFor = (cabinetTypeCode: string): number | null =>
    cabinetTypes.find((type) => type.code === cabinetTypeCode)?.default_height_mm ?? null;
  const defaultDepthFor = (cabinetTypeCode: string): number | null =>
    cabinetTypes.find((type) => type.code === cabinetTypeCode)?.default_depth_mm ?? null;

  /** 内部界面一律中文柜型名（代码只在数据里用，不给用户看） */
  const cabinetTypeName = (cabinetTypeCode: string): string =>
    cabinetTypes.find((type) => type.code === cabinetTypeCode)?.name ?? cabinetTypeCode;

  /**
   * 房间名候选：本方案已经用过的靠前，再补常用叫法。
   * 房间是自由文本（老系统的习惯），候选只是省打字，不限制输入。
   */
  const roomSuggestions = useMemo(() => {
    const used = new Map<string, number>();
    for (const item of detail?.items ?? []) {
      const room = item.room?.trim();
      if (room) used.set(room, (used.get(room) ?? 0) + 1);
    }
    const usedSorted = [...used.entries()].sort((a, b) => b[1] - a[1]).map(([room]) => room);
    // 方案里用过的排最前（这些是自由文本，原样列出），后面补常用叫法的中英对照
    return [
      ...usedSorted.map((room) => ({ value: room, label: roomOptionText(room) })),
      ...ROOM_LABELS.filter((entry) => !used.has(entry.zh) && !used.has(entry.en)).map((entry) => ({
        value: entry.zh,
        label: `${entry.zh} · ${entry.en}`,
      })),
    ];
  }, [detail?.items]);

  /** 本页所有柜体：和画布同一口径（图纸 + 页码），用户看到的这一页就是它 */
  const itemsOnCurrentPage = useMemo(
    () =>
      (detail?.items ?? []).filter(
        (item) => item.drawing_id === selected?.drawing_id && item.page_index === selected?.page_index,
      ),
    [detail?.items, selected?.drawing_id, selected?.page_index],
  );

  /** 改房间名：只写 room，不重算价格（房间不参与计价） */
  const commitRoom = async (itemId: string, raw: string) => {
    const room = raw.trim() === "" ? null : raw.trim().slice(0, 64);
    const current = detail?.items.find((item) => item.id === itemId);
    if (!current || (current.room ?? null) === room) return;
    setBusy(true);
    try {
      await api.updateItem(itemId, { room });
      await load();
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "改房间失败");
    } finally {
      setBusy(false);
    }
  };

  /** 同一个房间往往画了好几个框：一键把房间名套到本页所有柜体 */
  const applyRoomToPage = async () => {
    if (!selected) return;
    const room = roomDraft.trim() === "" ? null : roomDraft.trim().slice(0, 64);
    const targets = itemsOnCurrentPage.filter((item) => (item.room ?? null) !== room);
    if (targets.length === 0) {
      setStatus("本页柜体的房间名已经是这个了");
      return;
    }
    setBusy(true);
    try {
      for (const item of targets) await api.updateItem(item.id, { room });
      await load();
      setStatus(`已把房间「${room ?? "清空"}」套用到本页 ${targets.length} 个柜体`);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "改房间失败");
    } finally {
      setBusy(false);
    }
  };

  /** 批量替换：当前方案里该槽位已经用过的组件（用于"只替换当前是 X 的行"） */
  const slotCatalogOptions = (() => {
    const seen = new Map<string, string>();
    for (const component of detail?.components ?? []) {
      if (component.slot !== bulkDraft.slot) continue;
      seen.set(component.catalog_code, component.name_snapshot);
    }
    return [...seen.entries()].map(([code, name]) => ({ code, name }));
  })();

  const runBulkReplace = async () => {
    if (!bulkDraft.catalogItemId) return;
    const scope =
      bulkDraft.scopeKind === "plan"
        ? ({ type: "plan" } as const)
        : bulkDraft.scopeKind === "cabinet_type"
          ? ({ type: "cabinet_type", code: bulkDraft.cabinetTypeCode } as const)
          : selected
            ? { type: "items" as const, itemIds: [selected.id] }
            : null;
    if (!scope) {
      setBulkFailed(true);
      setBulkResult("先在画布上选中一个柜体，或改用整单 / 按柜型范围");
      return;
    }
    setBusy(true);
    try {
      const result = await api.bulkReplace(planId, {
        scope,
        slot: bulkDraft.slot,
        catalog_item_id: bulkDraft.catalogItemId,
        ...(bulkDraft.fromCode ? { from_catalog_code: bulkDraft.fromCode } : {}),
      });
      const skipped =
        result.skipped.length > 0
          ? `，跳过 ${result.skipped.length} 个（${result.skipped
              .slice(0, 3)
              .map((entry) => entry.label)
              .join("、")}）`
          : "";
      setBulkFailed(false);
      setBulkResult(`已替换 ${result.affectedItems} 个柜体、${result.changedRows} 行${skipped}，整单已重算`);
      await load();
    } catch (caught) {
      setBulkFailed(true);
      setBulkResult(caught instanceof Error ? caught.message : "替换失败");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 批量加项：纸箱 / 木箱这类"每组柜子都要一件"的项目，一次加到整单或某个柜型上。
   * 默认跳过已经有这个项目的柜体，所以重复点不会变成两份。
   */
  const runBulkAdd = async () => {
    if (!bulkDraft.catalogItemId) return;
    const scope =
      bulkDraft.scopeKind === "plan"
        ? ({ type: "plan" } as const)
        : bulkDraft.scopeKind === "cabinet_type"
          ? ({ type: "cabinet_type", code: bulkDraft.cabinetTypeCode } as const)
          : selected
            ? { type: "items" as const, itemIds: [selected.id] }
            : null;
    if (!scope) {
      setBulkFailed(true);
      setBulkResult("先在画布上选中一个柜体，或改用整单 / 按柜型范围");
      return;
    }
    const quantity = Number(bulkQuantity);
    setBusy(true);
    try {
      const result = await api.bulkAdd(planId, {
        scope,
        slot: slotForCategory(bulkDraft.category),
        catalog_item_id: bulkDraft.catalogItemId,
        quantity_per_item: Number.isFinite(quantity) && quantity >= 0 ? quantity : 1,
      });
      const skipped =
        result.skipped.length > 0
          ? `，跳过 ${result.skipped.length} 个（${result.skipped
              .slice(0, 3)
              .map((entry) => entry.label)
              .join("、")}）`
          : "";
      setBulkFailed(false);
      setBulkResult(`已加到 ${result.addedItems} 个柜体、${result.addedRows} 行${skipped}，整单已重算`);
      await load();
    } catch (caught) {
      setBulkFailed(true);
      setBulkResult(caught instanceof Error ? caught.message : "加项失败");
    } finally {
      setBusy(false);
    }
  };

  /** 选了台面这类"长×深"的组件时，尺寸标签把"宽"显示成"长"，省得填错格子 */
  const widthLabel = components.some(
    (component) => component.calc_method === "area_factor" && componentParams(component).areaFrom === "wd",
  )
    ? "长"
    : "宽";
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; items: CatalogItemRow[] }>();
    for (const item of catalog) {
      const key = item.category_code.split(".")[0]!;
      const entry = map.get(key) ?? { name: item.category_name.split("·")[0]!.trim(), items: [] };
      entry.items.push(item);
      map.set(key, entry);
    }
    return [...map.entries()];
  }, [catalog]);

  const run = async (action: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    try {
      await action();
      await load();
      if (message) setStatus(message);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败");
    } finally {
      setBusy(false);
    }
  };

  /**
   * 拖拽落点：光标落在某一条的**上半** → 插到它前面，**下半** → 插到它后面；
   * 落在列表空白处（最后一条下面）→ 排到最后。落点跟着光标走，用户拖到哪就看到哪。
   */
  const dropSlotFrom = (event: { clientY: number; target: EventTarget | null }) => {
    const list = detail?.items ?? [];
    if (list.length === 0) return null;
    const row = (event.target as HTMLElement | null)?.closest?.("[data-item-row]") as HTMLElement | null;
    const id = row?.dataset.itemId;
    if (!row || !id || !list.some((entry) => entry.id === id)) {
      const last = list[list.length - 1]!;
      return { id: last.id, edge: "after" as const };
    }
    const rect = row.getBoundingClientRect();
    return {
      id,
      edge: (event.clientY > rect.top + rect.height / 2 ? "after" : "before") as "before" | "after",
    };
  };

  const stopAutoScroll = () => {
    if (!autoScrollRef.current) return;
    window.clearInterval(autoScrollRef.current.timer);
    autoScrollRef.current = null;
  };

  /**
   * 拖着条目走到列表上/下边缘时自动滚动，并把落点跟着重算——
   * 否则条目一多（18 个柜体就会超过一屏），想挪到看不见的位置只能先松开再滚。
   * 滚动条挪动的瞬间光标底下换了一条，所以要自己按新位置算一次落点。
   */
  const autoScrollNear = (node: HTMLElement, y: number) => {
    const rect = node.getBoundingClientRect();
    const step = y < rect.top + 32 ? -1 : y > rect.bottom - 32 ? 1 : 0;
    if (step === 0) {
      stopAutoScroll();
      return;
    }
    if (autoScrollRef.current?.node === node) return; // 已经在滚了，别反复重启
    stopAutoScroll();
    const timer = window.setInterval(() => {
      node.scrollTop += step * 14;
      const point = dragPointRef.current;
      if (!point) return;
      setDropSlot(dropSlotFrom({ clientY: point.y, target: document.elementFromPoint(point.x, point.y) }));
    }, 45);
    autoScrollRef.current = { timer, node };
  };

  /**
   * 调整条目次序：先把本地列表改成新次序（松手立刻看到结果），再提交给服务端。
   * 失败就把服务端的真实次序读回来，不让界面停在一个没存下来的顺序上。
   * 只动顺序、不动编号（不变量 §19）。
   */
  const moveItem = async (fromId: string, toId: string, edge: "before" | "after") => {
    const list = detail?.items ?? [];
    const from = list.findIndex((entry) => entry.id === fromId);
    if (from < 0 || fromId === toId) return;
    const without = list.filter((entry) => entry.id !== fromId);
    const anchor = without.findIndex((entry) => entry.id === toId);
    if (anchor < 0) return;
    const insertAt = edge === "after" ? anchor + 1 : anchor;
    if (insertAt === from) return; // 原位放下，不必打扰服务端
    const moved = list[from]!;
    const next = [...without.slice(0, insertAt), moved, ...without.slice(insertAt)];

    setDetail((current) => (current ? { ...current, items: next } : current));
    try {
      await api.reorderItems(
        planId,
        next.map((entry) => entry.id),
      );
      setError(null);
      setStatus(`${moved.label} 已挪到第 ${insertAt + 1} 位`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "排序失败");
      await load();
    }
  };

  /** 键盘等价操作：选中某条后 Alt + ↑ / ↓ 把它上移 / 下移一位（拖拽对键盘用户不可用）。 */
  const moveItemByStep = (itemId: string, step: -1 | 1) => {
    const list = detail?.items ?? [];
    const index = list.findIndex((entry) => entry.id === itemId);
    if (index < 0) return;
    const neighbour = list[index + step];
    if (!neighbour) return;
    void moveItem(itemId, neighbour.id, step < 0 ? "before" : "after");
  };

  /** 只把这一个条目和它的明细并回本地状态：不重取整表，框不会跳、不会闪。 */
  const applyComputation = (result: {
    item: PlanItemRow;
    components: PlanItemComponentRow[];
    totals: { costCents: number; priceCents: number; pendingItems: number };
  }) => {
    setDetail((current) =>
      current
        ? {
            ...current,
            plan: {
              ...current.plan,
              total_cost_cents: result.totals.costCents,
              total_price_cents: result.totals.priceCents,
            },
            items: current.items.some((item) => item.id === result.item.id)
              ? current.items.map((item) => (item.id === result.item.id ? result.item : item))
              : [...current.items, result.item],
            components: [
              ...current.components.filter((component) => component.item_id !== result.item.id),
              ...result.components,
            ],
          }
        : current,
    );
  };

  const createFromBox = (rect: BoxRect) => {
    const tempId = `tmp_${Math.random().toString(36).slice(2, 10)}`;
    /*
     * 先按"现有最大编号 +1"预演一个编号（和新建接口同一份规则）：
     * 这样删过条目之后再画框，占位框显示的编号就是服务端将要给的那个，不会先闪一个重号的 A03。
     */
    const label = nextPlanItemLabel([
      ...(detail?.items ?? []).map((item) => item.label),
      ...pending.map((item) => item.label),
    ]);
    // 1) 立刻画在图上（虚线 + "创建中"），用户看到的是"马上就出来了"
    setPending((current) => [
      ...current,
      {
        id: tempId,
        label,
        cabinet_type_code: "base",
        size_status: "pending",
        drawing_id: activeDrawing?.id ?? null,
        page_index: pageIndex,
        ...rect,
      },
    ]);
    // 2) 后台创建 + 套默认组件算价，完成后原地替换占位框
    void (async () => {
      try {
        /*
         * 平面图上画出来的矩形只能量出「长 × 深」——高在平面图里看不到，必须人工填。
         * 所以：框的横向 = 长 → width_mm；框的纵向 = 深 → depth_mm；高留空等用户填。
         */
        const widthMm =
          mmPerPageUnit && currentPage
            ? Math.round(rect.box_w * currentPage.width * mmPerPageUnit)
            : undefined;
        const depthMm =
          mmPerPageUnit && currentPage
            ? Math.round(rect.box_h * currentPage.height * mmPerPageUnit)
            : undefined;
        /*
         * 高、深都给一个**可改的默认值**（2026-10-21 客户要求，美标尺寸）：
         * - 高：平面图里永远量不出来 → 用柜型默认高；
         * - 深：没有标尺时量不出来 → 用柜型默认深；有标尺时以图上量为准（场地实测优先于默认值）。
         */
        const defaultHeight = defaultHeightFor("base");
        const defaultDepth = defaultDepthFor("base");
        const filledDepth = depthMm ?? defaultDepth ?? undefined;
        /*
         * 房间名继承：同一页通常就是同一个房间，画第一个框时填一次，
         * 后面在同一页画的框自动跟着，不用每个框都重打一遍。
         */
        const samePageRooms = (detail?.items ?? []).filter(
          (item) => item.drawing_id === activeDrawing?.id && item.page_index === pageIndex,
        );
        // 只继承**同一张图纸、同一页**的房间名：换页/换图就是换房间，不要从上页串过来
        const inheritedRoom = samePageRooms.find((item) => item.room)?.room ?? null;
        const created = await api.createItem(planId, {
          ...rect,
          page_index: pageIndex,
          drawing_id: activeDrawing?.id ?? null,
          cabinet_type_code: "base",
          room: inheritedRoom,
          ...(widthMm ? { width_mm: widthMm } : {}),
          ...(filledDepth ? { depth_mm: filledDepth } : {}),
          ...(defaultHeight ? { height_mm: defaultHeight } : {}),
        });
        const computed = await api.recompute(created.item.id);
        setPending((current) => current.filter((item) => item.id !== tempId));
        applyComputation(computed);
        setSelectedId(computed.item.id);
        setFocusHeightFor(null);
        setStatus(
          widthMm && depthMm
            ? `${computed.item.label}：长 ${widthMm} × 深 ${depthMm}（标尺算出），高用柜型默认 ${defaultHeight ?? "—"}mm（可改）`
            : `${computed.item.label}：高/深已按柜型默认 ${defaultHeight ?? "—"} / ${filledDepth ?? "—"}mm 填好（可改），补长即出价`,
        );
        setError(null);
      } catch (caught) {
        setPending((current) => current.filter((item) => item.id !== tempId));
        setError(caught instanceof Error ? caught.message : "新建条目失败");
      }
    })();
  };

  const patchSelected = async (patch: Record<string, unknown>) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.updateItem(selected.id, patch);
      applyComputation(await api.recompute(selected.id));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      // PDF 走 pdf.js 读页数和页面尺寸；图片只有一页，直接读自然尺寸
      const isImage = file.type.startsWith("image/");
      const sizes = isImage
        ? [
            await new Promise<{ page_index: number; width: number; height: number; rotation: number }>(
              (resolve, reject) => {
                const image = new Image();
                const objectUrl = URL.createObjectURL(file);
                image.onload = () => {
                  URL.revokeObjectURL(objectUrl);
                  resolve({
                    page_index: 0,
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                    rotation: 0,
                  });
                };
                image.onerror = () => {
                  URL.revokeObjectURL(objectUrl);
                  reject(new Error("图片读取失败"));
                };
                image.src = objectUrl;
              },
            ),
          ]
        : await readPageSizes((await file.arrayBuffer()).slice(0));
      const uploaded = await api.uploadDrawing(planId, file, sizes.length);
      if (!uploaded.deduplicated) await api.reportPages(uploaded.drawing.id, sizes);
      setDrawingId(uploaded.drawing.id);
      setPageIndex(0);
      // 新图纸成为当前图纸：上一张图纸选中的条目要取消，避免面板和画布对不上
      setSelectedId(null);
      setStatus(
        uploaded.deduplicated ? "这份图纸已存在，直接复用" : `已上传 ${file.name}（${sizes.length} 页）`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "上传失败");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === "r") setTool("draw");
      else if (key === "v") setTool("select");
      else if (key === "m") ensureRuler();
      else if (key === "escape") {
        setTool("select");
        setSelectedId(null);
      } else if ((event.key === "Delete" || event.key === "Backspace") && selectedId) {
        event.preventDefault();
        void run(() => api.deleteItem(selectedId), "已删除条目").then(() => setSelectedId(null));
      } else if (key === "g" && !event.metaKey && !event.ctrlKey)
        void run(() => api.generate(planId, "fill_missing"), "已按柜型生成报价");
      else if (key === "+" || key === "=")
        setView((current) => ({ ...current, scale: Math.min(MAX_SCALE, current.scale * 1.2) }));
      else if (key === "-")
        setView((current) => ({ ...current, scale: Math.max(MIN_SCALE, current.scale / 1.2) }));
      else if (key === "0") setView({ scale: 1, x: 0, y: 0 });
      else if (event.key.startsWith("Arrow") && selected) {
        event.preventDefault();
        const step = event.shiftKey ? 0.02 : 0.005;
        const dx = event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
        const dy = event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
        void api
          .updateItem(selected.id, {
            box_x: Math.min(Math.max(selected.box_x + dx, 0), 1 - selected.box_w),
            box_y: Math.min(Math.max(selected.box_y + dy, 0), 1 - selected.box_h),
          })
          .then(load);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  if (!detail)
    return <p className="p-6 text-sm text-[var(--color-muted-foreground)]">{error ?? "加载中…"}</p>;

  const plan = detail.plan;
  const pendingCount = detail.items.filter((item) => item.size_status === "pending").length;
  /** 换算器的实时结果：只做显示，不写任何数据 */
  const converterResult = parseLength(converterDraft.value, converterDraft.unit);
  const converterMm = converterResult.ok ? converterResult.mm : null;

  return (
    <div className="flex h-full min-h-0">
      {/*
        批量操作：① 换材料（柜体/门板/台面/五金/配件）② 加项目（纸箱、木箱、组装…）。
        用原生 <dialog> + showModal()：Esc 关闭、焦点锁在弹窗里、背景自动 inert，都由浏览器负责，
        比自己写的 div + 手工焦点管理更可靠（原来这些都没有）。
      */}
      <dialog
        ref={bulkDialogRef}
        aria-labelledby="bulk-dialog-title"
        onClose={() => setBulkOpen(false)}
        onCancel={() => setBulkOpen(false)}
        className="z-50 m-auto w-[600px] max-w-[calc(100vw-2rem)] rounded-xl border border-[var(--color-border)] bg-white p-4 text-[var(--color-foreground)] shadow-xl backdrop:bg-black/30"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span id="bulk-dialog-title" className="sr-only">
                批量替换组件与批量加项
              </span>
              {(
                [
                  ["replace", "批量替换组件"],
                  ["add", "批量加项"],
                ] as const
              ).map(([mode, label]) => (
                <button
                  type="button"
                  key={mode}
                  onClick={() => {
                    setBulkMode(mode);
                    setBulkResult(null);
                    // 两种模式的分类含义不同（替换=槽位分类，加项=要加的项目分类），切换时都清空目标
                    setBulkDraft({
                      ...bulkDraft,
                      catalogItemId: "",
                      query: "",
                      ...(mode === "add" ? { category: "accessory" } : {}),
                    });
                  }}
                  className={`rounded-md px-2 py-1 text-sm ${
                    bulkMode === mode
                      ? "bg-[var(--color-primary)] font-semibold text-white"
                      : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="button" className="text-xs hover:underline" onClick={() => setBulkOpen(false)}>
              关闭
            </button>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-muted-foreground)]">作用范围</span>
            <div className="flex gap-1">
              {(
                [
                  ["plan", "整单全部柜体"],
                  ["cabinet_type", "按柜型"],
                  ["selected", "当前选中条目"],
                ] as const
              ).map(([kind, label]) => (
                <button
                  type="button"
                  key={kind}
                  onClick={() => setBulkDraft({ ...bulkDraft, scopeKind: kind })}
                  className={`rounded-full px-3 py-1 text-xs ${
                    bulkDraft.scopeKind === kind
                      ? "bg-[var(--color-primary)] text-white"
                      : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {bulkDraft.scopeKind === "cabinet_type" ? (
              <Select
                aria-label="柜型范围"
                className="mt-1"
                value={bulkDraft.cabinetTypeCode}
                onChange={(event) => setBulkDraft({ ...bulkDraft, cabinetTypeCode: event.target.value })}
              >
                {cabinetTypes.map((type) => (
                  <option key={type.code} value={type.code}>
                    {type.name}（{type.code}）
                  </option>
                ))}
              </Select>
            ) : null}
            {bulkDraft.scopeKind === "selected" ? (
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {selected
                  ? `${bulkMode === "replace" ? "只替换" : "只加到"} ${selected.label}`
                  : "还没有选中条目：先在画布上点一个框"}
              </p>
            ) : null}
          </div>

          {bulkMode === "replace" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted-foreground)]">要替换的槽位</span>
                <Select
                  aria-label="槽位"
                  value={bulkDraft.slot}
                  onChange={(event) => {
                    const slot = event.target.value;
                    const category =
                      slot === "box"
                        ? "cabinet"
                        : slot === "countertop"
                          ? "countertop"
                          : slot === "panel"
                            ? "panel"
                            : slot;
                    // 换槽位就把"换成哪个"清掉：不能拿上一类材料去填新槽位
                    setBulkDraft({ ...bulkDraft, slot, category, fromCode: "", catalogItemId: "" });
                  }}
                >
                  <option value="box">柜体（柜体板）</option>
                  <option value="door">门板</option>
                  <option value="countertop">台面</option>
                  <option value="panel">见光板</option>
                  <option value="hardware">五金（按百分比）</option>
                  <option value="accessory">配件（按件）</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted-foreground)]">只替换哪一款（可选）</span>
                <Select
                  aria-label="来源组件"
                  value={bulkDraft.fromCode}
                  onChange={(event) => setBulkDraft({ ...bulkDraft, fromCode: event.target.value })}
                >
                  <option value="">该槽位全部行</option>
                  {slotCatalogOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      当前是 {option.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-3">
              <div className="flex w-40 flex-col gap-1">
                <span className="text-xs text-[var(--color-muted-foreground)]">每组柜子数量</span>
                <Input
                  aria-label="每组数量"
                  type="number"
                  min={0}
                  value={bulkQuantity}
                  onChange={(event) => setBulkQuantity(event.target.value)}
                />
              </div>
              <p className="pb-2 text-xs text-[var(--color-muted-foreground)]">
                纸箱、木箱这类"每组柜子一件"的项目填 1；重复点不会变成两份。
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs text-[var(--color-muted-foreground)]">
              {bulkMode === "replace" ? "换成（目录里选一个）" : "加哪一项（目录里选一个）"}
            </span>
            {bulkMode === "replace" ? (
              // 替换模式下分类由槽位决定，不让选：否则会把五金填进门板位、门板料填进柜体位
              <p className="rounded-md bg-[var(--color-muted)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                只列出「{CATEGORY_LABEL[bulkDraft.category] ?? bulkDraft.category}
                」类组件（槽位决定分类）
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {Object.entries(CATEGORY_LABEL).map(([code, label]) => (
                  <button
                    type="button"
                    key={code}
                    onClick={() => setBulkDraft({ ...bulkDraft, category: code, catalogItemId: "" })}
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      bulkDraft.category === code
                        ? "bg-[var(--color-primary)] text-white"
                        : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <Input
              aria-label="搜索目标组件"
              placeholder="搜索名称或代码"
              value={bulkDraft.query}
              onChange={(event) => setBulkDraft({ ...bulkDraft, query: event.target.value })}
            />
            <div className="max-h-48 overflow-auto rounded-md border border-[var(--color-border)]">
              {catalog
                .filter((item) => (item.category_code.split(".")[0] ?? "") === bulkDraft.category)
                .filter((item) =>
                  bulkDraft.query.trim() === ""
                    ? true
                    : `${item.name}${item.code}`.toLowerCase().includes(bulkDraft.query.trim().toLowerCase()),
                )
                .map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => setBulkDraft({ ...bulkDraft, catalogItemId: item.id })}
                    className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-xs ${
                      bulkDraft.catalogItemId === item.id
                        ? "bg-[var(--color-primary)] text-white"
                        : "hover:bg-[var(--color-muted)]"
                    }`}
                  >
                    <span className="truncate">{item.name}</span>
                    <span className="ml-2 shrink-0 tabular-nums opacity-70">
                      {item.calc_method === "percent_of_slots"
                        ? "按百分比"
                        : `${formatMoney(item.unit_price_cents, "CNY")}/${item.unit}`}
                    </span>
                  </button>
                ))}
            </div>
          </div>

          {bulkResult ? (
            <p
              role={bulkFailed ? "alert" : "status"}
              className={`text-xs ${bulkFailed ? "text-[var(--color-destructive)]" : "text-emerald-700"}`}
            >
              {bulkResult}
            </p>
          ) : null}
          <Button
            size="sm"
            disabled={busy || !bulkDraft.catalogItemId}
            onClick={() => void (bulkMode === "replace" ? runBulkReplace() : runBulkAdd())}
          >
            {bulkMode === "replace" ? "执行替换并重算整单" : "加到这些柜体并重算整单"}
          </Button>
          <p className="text-[12px] text-[var(--color-muted-foreground)]">
            {bulkMode === "replace"
              ? "只换材料，不改变条目数量与尺寸；单价按新组件的当前目录价重新快照，五金/百分比行会按新基数重算。"
              : "按件项目（纸箱、木箱、组装、配件…）加到选中的每组柜子上；已经有这一项的柜体自动跳过。"}
          </p>
        </div>
      </dialog>
      {/*
        长度换算器：看图时随手换一个数，不用离开当前方案。
        只是"看着换"，不写库——要真影响报价，还是得填进尺寸输入框。
      */}
      <dialog
        ref={converterDialogRef}
        aria-labelledby="converter-dialog-title"
        onClose={() => setConverterOpen(false)}
        onCancel={() => setConverterOpen(false)}
        className="z-50 m-auto w-[420px] max-w-[calc(100vw-2rem)] rounded-xl border border-[var(--color-border)] bg-white p-4 text-[var(--color-foreground)] shadow-xl backdrop:bg-black/30"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span id="converter-dialog-title" className="font-medium">
              长度换算器
            </span>
            <button type="button" className="text-xs hover:underline" onClick={() => setConverterOpen(false)}>
              关闭
            </button>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-xs text-[var(--color-muted-foreground)]">填图纸上的数字</span>
              <Input
                aria-label="要换算的数值"
                autoFocus
                inputMode="decimal"
                value={converterDraft.value}
                onChange={(event) =>
                  setConverterDraft((current) => ({ ...current, value: event.target.value }))
                }
              />
            </div>
            <div className="flex w-28 flex-col gap-1">
              <span className="text-xs text-[var(--color-muted-foreground)]">单位</span>
              <Select
                aria-label="换算单位"
                value={converterDraft.unit}
                onChange={(event) =>
                  setConverterDraft((current) => ({
                    ...current,
                    unit: event.target.value as LengthUnit,
                  }))
                }
              >
                {LENGTH_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {LENGTH_UNIT_META[unit].name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {converterMm === null ? (
            <p className="text-xs text-[var(--color-destructive)]">
              {converterResult.ok ? "" : converterResult.error}
            </p>
          ) : (
            <div className="flex flex-col gap-1 text-sm">
              {(
                [
                  ["毫米", `${Math.round(converterMm)} mm`],
                  ["厘米", `${formatLength(converterMm, "cm")} cm`],
                  ["米", `${formatLength(converterMm, "m")} m`],
                  ["英寸", `${formatLength(converterMm, "in")}"`],
                  ["英尺英寸", formatFeetInches(converterMm)],
                ] as const
              ).map(([label, text]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-[var(--color-muted-foreground)]">{label}</span>
                  <span className="tabular-nums">{text}</span>
                </div>
              ))}
            </div>
          )}
          <Separator />
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-muted-foreground)]">常用尺寸速查</span>
            <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs tabular-nums">
              {INCH_QUICK_REFERENCE.map((inch) => (
                <span key={inch}>
                  {inch}" = {Math.round(inch * MM_PER_INCH)} mm
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            换算器只是参考，不影响任何数据。真正落到报价上的是尺寸输入框——切到「英寸」后写的 24 / 10-6
            会按这里的比例换算成毫米再入库。
          </p>
        </div>
      </dialog>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
          event.target.value = "";
        }}
      />

      {/* 第 1 栏：图纸与页码导航 */}
      <PageRail
        drawings={detail.drawings}
        activeDrawingId={drawingId}
        url={activeDrawing ? `/api/v1/media/${activeDrawing.file_key}` : null}
        pageIndex={pageIndex}
        pageCount={pageCount}
        // 只数当前图纸的框：图片型图纸每张都有自己的第 0 页，混着数会显示成别的图上的框
        boxCounts={detail.items.reduce<Record<number, number>>((counts, item) => {
          if (item.drawing_id !== drawingId) return counts;
          counts[item.page_index] = (counts[item.page_index] ?? 0) + 1;
          return counts;
        }, {})}
        onSelectPage={setPageIndex}
        onSelectDrawing={(id) => {
          setDrawingId(id);
          setPageIndex(0);
          // 选中的条目可能属于上一张图纸：换图后右侧面板要跟着清空，别停在看不见的框上
          setSelectedId(null);
        }}
        onUpload={() => fileRef.current?.click()}
      />

      {/* 第 2 栏：画布 */}
      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] bg-white px-3 py-2">
          {/* 报价名字：点一下就能改（回车保存 / Esc 取消） */}
          {renamingPlan ? (
            <Input
              aria-label="报价名称"
              autoFocus
              className="h-7 w-52 text-sm"
              value={planNameDraft}
              onChange={(event) => setPlanNameDraft(event.target.value)}
              onBlur={() => {
                const next = planNameDraft.trim();
                setRenamingPlan(false);
                if (next === "" || next === plan.name) return;
                void run(() => api.updatePlan(plan.id, { name: next }), `报价已改名为「${next}」`);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setPlanNameDraft(plan.name);
                  setRenamingPlan(false);
                }
              }}
            />
          ) : (
            <button
              type="button"
              title="点击修改报价名称"
              className="rounded px-1 -mx-1 text-sm font-medium hover:bg-[var(--color-muted)]"
              onClick={() => {
                setPlanNameDraft(plan.name);
                setRenamingPlan(true);
              }}
            >
              {plan.name}
            </button>
          )}
          <Badge tone={pendingCount > 0 ? "warn" : "ok"}>
            {pendingCount > 0 ? `${pendingCount} 个未填尺寸（不计入合计）` : "尺寸已填齐"}
          </Badge>
          {/* 一次性工具：点一次画一个框，画完自动回到选择；勾「连续」才保持 */}
          <div className="flex items-center gap-1 rounded-md border border-[var(--color-border)] p-0.5">
            <Button variant={tool === "draw" ? "default" : "ghost"} size="sm" onClick={() => setTool("draw")}>
              画框 R
            </Button>
            <Button variant={rulerSelected ? "default" : "ghost"} size="sm" onClick={ensureRuler}>
              标尺 M
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setBulkOpen(true)}>
              批量替换
            </Button>
            <label className="flex cursor-pointer items-center gap-1 px-1.5 text-xs text-[var(--color-muted-foreground)]">
              <input
                type="checkbox"
                checked={continuous}
                onChange={(event) => {
                  setContinuous(event.target.checked);
                  if (event.target.checked) setTool("draw");
                }}
              />
              连续
            </label>
          </div>
          {/*
            尺寸输入单位：只有「输入 / 显示的口径」变，落库与计价永远是毫米。
            图纸是英制时切到「英寸」，标尺长度和长/深/高都可以按 24、24 1/2、10-6 写。
          */}
          <div className="flex items-center gap-1 rounded-md border border-[var(--color-border)] p-0.5 text-xs">
            <span className="px-1 text-[var(--color-muted-foreground)]">单位</span>
            {LENGTH_UNITS.map((unit) => (
              <button
                type="button"
                key={unit}
                title={LENGTH_UNIT_META[unit].hint}
                aria-pressed={inputUnit === unit}
                onClick={() => changeInputUnit(unit)}
                className={`rounded px-1.5 py-0.5 ${
                  inputUnit === unit
                    ? "bg-[var(--color-primary)] font-medium text-white"
                    : "hover:bg-[var(--color-muted)]"
                }`}
              >
                {LENGTH_UNIT_META[unit].label}
              </button>
            ))}
            <button
              type="button"
              className="rounded px-1.5 py-0.5 hover:bg-[var(--color-muted)]"
              title="英寸 ⇄ 毫米 换算器"
              onClick={() => setConverterOpen(true)}
            >
              换算
            </button>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setView((current) => ({ ...current, scale: Math.max(MIN_SCALE, current.scale / 1.2) }))
              }
            >
              −
            </Button>
            <button
              type="button"
              className="w-12 text-center hover:underline"
              onClick={() => setView({ scale: 1, x: 0, y: 0 })}
              title="点击重置视图"
            >
              {Math.round(view.scale * 100)}%
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setView((current) => ({ ...current, scale: Math.min(MAX_SCALE, current.scale * 1.2) }))
              }
            >
              +
            </Button>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs">
            {/* 标尺状态：有就显示长度 + 清除；没有就提示点标尺按钮 */}
            {ruler ? (
              <span className="flex items-center gap-1 text-red-600">
                标尺
                {/* 长度就放在工具条上直接改，不用先选中标尺 */}
                <Input
                  ref={rulerLengthRef}
                  aria-label="标尺长度"
                  // 单位是英寸时要能写 10'6" / 24 1/2，number 类型收不下这些字符
                  type={inputUnit === "in" ? "text" : "number"}
                  inputMode="decimal"
                  title={LENGTH_UNIT_META[inputUnit].hint}
                  className="h-7 w-24 text-right"
                  key={`ruler-${pageIndex}-${ruler.lengthMm}-${inputUnit}`}
                  defaultValue={formatLength(ruler.lengthMm, inputUnit)}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={(event) => {
                    if (!drawingId) return;
                    // 按当前单位解析后换算成毫米再存：库里永远只有毫米与比例
                    const parsed = parseLength(event.target.value, inputUnit);
                    if (!parsed.ok) {
                      setError(parsed.error);
                      return;
                    }
                    const lengthMm = roundToMm(parsed.mm);
                    if (lengthMm === ruler.lengthMm) return;
                    void api.saveRuler(drawingId, pageIndex, { ...ruler, lengthMm }).then(async () => {
                      const refreshed = await api.drawing(drawingId);
                      setPages(refreshed.pages);
                      setRulerJustCreated(false);
                      setError(null);
                      setStatus(`标尺已改为 ${Math.round(lengthMm)}mm，之后画的框按新比例算`);
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                />
                <span title={`1 ${LENGTH_UNIT_META[inputUnit].label} 已换算成毫米落库`}>
                  {LENGTH_UNIT_META[inputUnit].label}
                </span>
                <button
                  type="button"
                  className="text-[var(--color-muted-foreground)] hover:underline"
                  onClick={() => {
                    if (!drawingId) return;
                    void api.clearRuler(drawingId, pageIndex).then(async () => {
                      const refreshed = await api.drawing(drawingId);
                      setPages(refreshed.pages);
                      setStatus("标尺已清除");
                    });
                  }}
                >
                  清除
                </button>
              </span>
            ) : (
              <span className="text-[var(--color-muted-foreground)]">
                未设标尺（点「标尺」会在画布中间放一条线，拖到位再填长度）
              </span>
            )}
            {/*
              role="status"（polite live region）让读屏播报"已替换 5 个柜体""标尺已改"这类结果。
              区域常驻在 DOM 里、只换文字，动态插入的区域播报不可靠。
            */}
            <span role="status" aria-live="polite" className="text-emerald-700">
              {status}
            </span>
            <span className="text-[var(--color-muted-foreground)]">
              成本 {formatMoney(plan.total_cost_cents, "CNY")} → 报价{" "}
              {formatMoney(plan.total_price_cents, plan.currency)}
            </span>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void run(() => api.generate(planId, "fill_missing"), "已生成报价")}
            >
              生成报价 G
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(
                  `${window.location.origin}${window.location.pathname}#/plans/${planId}/print`,
                  "_blank",
                  "noopener",
                )
              }
            >
              客户版
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(
                  `${window.location.origin}${window.location.pathname}#/plans/${planId}/factory`,
                  "_blank",
                  "noopener",
                )
              }
            >
              工厂版
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-neutral-100 p-3">
          {error ? (
            <p role="alert" className="mb-2 text-sm text-[var(--color-destructive)]">
              {error}
            </p>
          ) : null}
          {activeDrawing ? (
            <PdfCanvas
              url={`/api/v1/media/${activeDrawing.file_key}`}
              imageUrl={
                activeDrawing.mime_type?.startsWith("image/")
                  ? `/api/v1/media/${activeDrawing.file_key}`
                  : null
              }
              pageIndex={pageIndex}
              view={view}
              onViewChange={setView}
              ruler={ruler}
              rulerSelected={rulerSelected}
              onRulerSelect={(selected) => {
                setRulerSelected(selected);
                if (selected) setSelectedId(null);
              }}
              onRulerEdit={() => {
                // 双击标尺 → 选中它并聚焦长度输入
                setRulerSelected(true);
                setSelectedId(null);
                setRulerJustCreated(true);
              }}
              onRulerChange={(segment) => {
                if (!drawingId || !ruler) return;
                // 拖动/旋转/拉伸只改变线段位置与长度，实际长度（mm）保持不变 →
                // 比例随之变化，之后画的框按新比例换算
                void api
                  .saveRuler(drawingId, pageIndex, { ...segment, lengthMm: ruler.lengthMm })
                  .then(async () => {
                    const refreshed = await api.drawing(drawingId);
                    setPages(refreshed.pages);
                  });
              }}
              onRotate={(itemId, rotationDeg) =>
                // 旋转只改角度，不影响价格：先本地生效，再写库
                void (() => {
                  setDetail((current) =>
                    current
                      ? {
                          ...current,
                          items: current.items.map((item) =>
                            item.id === itemId ? { ...item, rotation_deg: rotationDeg } : item,
                          ),
                        }
                      : current,
                  );
                  return api.updateItem(itemId, { rotation_deg: rotationDeg }).then(load);
                })()
              }
              drawMode={tool === "draw"}
              onDrawn={() => {
                // 一次性工具：画完这一框就回到选择模式（勾了「连续」则保持）
                if (!continuous) setTool("select");
              }}
              items={pageItems}
              selectedId={selectedId}
              onSelect={(id) => {
                setSelectedId(id);
                setLibraryId(null);
              }}
              onCreate={(rect) => void createFromBox(rect)}
              onUpdate={(itemId, rect) =>
                // 先【同步】写入新坐标，再发请求：否则松手后 React 会先用旧坐标渲染一帧，
                // 看起来就是"框往左上闪一下再跳回新位置"。价格不受位置影响，不需要重算。
                void (() => {
                  setDetail((current) =>
                    current
                      ? {
                          ...current,
                          items: current.items.map((item) =>
                            item.id === itemId ? { ...item, ...rect } : item,
                          ),
                        }
                      : current,
                  );
                  return api.updateItem(itemId, rect).then((result) =>
                    setDetail((current) =>
                      current
                        ? {
                            ...current,
                            items: current.items.map((item) => (item.id === itemId ? result.item : item)),
                          }
                        : current,
                    ),
                  );
                })()
              }
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-white px-8 py-10 text-center">
                <p className="text-sm font-medium">先上传 PDF 户型图</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  上传后按 R 在图上拖拽画框，每个框就是一个柜体
                </p>
                <Button className="mt-4" size="sm" onClick={() => fileRef.current?.click()}>
                  选择 PDF 文件
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 第 3 栏：组件导航 */}
      <aside className="flex w-64 shrink-0 flex-col border-l border-[var(--color-border)] bg-white">
        <div className="flex border-b border-[var(--color-border)] text-xs">
          {(["cabinets", "library"] as const).map((tab) => (
            <button
              type="button"
              key={tab}
              onClick={() => setNavTab(tab)}
              className={`flex-1 px-3 py-2 ${
                navTab === tab
                  ? "border-b-2 border-[var(--color-primary)] font-medium"
                  : "text-[var(--color-muted-foreground)]"
              }`}
            >
              {tab === "cabinets" ? `本方案柜体 ${detail.items.length}` : "目录浏览"}
            </button>
          ))}
        </div>
        {navTab === "cabinets" && detail.items.length > 1 ? (
          <p className="border-b border-[var(--color-border)] px-2 py-1.5 text-[12px] text-[var(--color-muted-foreground)]">
            按住条目拖动即可调整次序（选中后 Alt + ↑ / ↓ 也行）：编号不变，报价单与核价表跟着这个顺序走。
          </p>
        ) : null}
        {/*
          整个列表就是拖放区：行与行之间的缝隙、最后一条下面的空白也要接住 drop，
          所以落在容器上而不是某一行上。ARIA 里没有"可放置区域"这个角色，这里不硬塞 role；
          真正可交互的是行本身（<button>：点选、Enter/Space、Alt + ↑ / ↓ 排序都可用），
          拖拽只是锦上添花的鼠标操作。
        */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: 拖放区没有对应语义元素，见上 */}
        <div
          className="flex-1 overflow-auto p-2"
          onDragOver={(event) => {
            if (!dragItemIdRef.current) return;
            // 不 preventDefault 就收不到 drop：预览的落点也在这里算
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            dragPointRef.current = { x: event.clientX, y: event.clientY };
            setDropSlot(dropSlotFrom(event));
            autoScrollNear(event.currentTarget, event.clientY);
          }}
          onDrop={(event) => {
            event.preventDefault();
            const slot = dropSlotFrom(event) ?? dropSlot;
            const from = dragItemIdRef.current;
            stopAutoScroll();
            dragPointRef.current = null;
            dragItemIdRef.current = null;
            setDragItemId(null);
            setDropSlot(null);
            if (from && slot) void moveItem(from, slot.id, slot.edge);
          }}
        >
          {navTab === "cabinets"
            ? detail.items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  data-item-row
                  data-item-id={item.id}
                  aria-pressed={item.id === selectedId}
                  draggable
                  title="拖动调整次序（Alt + ↑ / ↓ 也可以）"
                  onDragStart={(event) => {
                    dragItemIdRef.current = item.id;
                    setDragItemId(item.id);
                    event.dataTransfer.effectAllowed = "move";
                    // Firefox 只有 setData 之后才真正开始拖拽，保留这行
                    event.dataTransfer.setData("text/plain", item.label);
                  }}
                  onDragEnd={() => {
                    stopAutoScroll();
                    dragPointRef.current = null;
                    dragItemIdRef.current = null;
                    setDragItemId(null);
                    setDropSlot(null);
                  }}
                  onClick={() => {
                    // 条目属于「图纸 + 页码」：点列表里的框，画布要跟着跳到它所在的图纸和页，
                    // 否则右侧面板在改一个画布上看不见的框。
                    if (item.drawing_id && item.drawing_id !== drawingId) setDrawingId(item.drawing_id);
                    if (item.page_index !== pageIndex) setPageIndex(item.page_index);
                    setSelectedId(item.id);
                    setLibraryId(null);
                  }}
                  onKeyDown={(event) => {
                    // 拖拽对键盘用户不可用，给一个等价操作：Alt + ↑ / ↓ 上移 / 下移一位
                    if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
                      event.preventDefault();
                      moveItemByStep(item.id, event.key === "ArrowUp" ? -1 : 1);
                    }
                  }}
                  className={`group relative mb-1 w-full cursor-grab rounded-md px-2 py-1.5 text-left text-sm active:cursor-grabbing ${
                    item.id === selectedId
                      ? "bg-[var(--color-primary)] text-white"
                      : "hover:bg-[var(--color-muted)]"
                  } ${dragItemId === item.id ? "opacity-40" : ""}`}
                >
                  {/* 落点提示线：贴着上/下边缘画，不占位、不推挤旁边的条目 */}
                  {dropSlot?.id === item.id ? (
                    <span
                      aria-hidden
                      className={`pointer-events-none absolute inset-x-0 h-0.5 rounded-full bg-[var(--color-primary)] ${
                        dropSlot.edge === "before" ? "-top-0.5" : "-bottom-0.5"
                      }`}
                    />
                  ) : null}
                  <div className="flex items-center justify-between gap-1">
                    <span className="flex min-w-0 items-center gap-1">
                      <GripIcon className="h-3 w-2.5 shrink-0 opacity-0 transition group-hover:opacity-60 group-focus-visible:opacity-60" />
                      <span className="font-medium">{item.label}</span>
                    </span>
                    <span className="shrink-0 text-[12px] opacity-80">
                      {cabinetTypeName(item.cabinet_type_code)}
                    </span>
                  </div>
                  {/* 尺寸与金额随时在变，用等宽数字，刷新时整列不会左右跳 */}
                  <div className="text-[12px] tabular-nums opacity-80">
                    {item.room ? `${item.room} · ` : ""}
                    {item.width_mm ?? "—"}×{item.height_mm ?? "—"} ·{" "}
                    {formatMoney(item.price_cents, plan.currency)}
                  </div>
                </button>
              ))
            : groups.map(([code, group]) => (
                <div key={code} className="mb-3">
                  <div className="flex items-baseline justify-between px-1 pb-1">
                    <span className="text-[12px] font-medium text-[var(--color-muted-foreground)]">
                      {CATEGORY_LABEL[code] ?? group.name}（{group.items.length}）
                    </span>
                    <span className="text-[12px] text-[var(--color-muted-foreground)]">
                      {SINGLE_VALUE_SLOTS.has(SLOT_BY_CATEGORY[code] ?? "") ? "单值" : "可叠加"}
                    </span>
                  </div>
                  {group.items.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => setLibraryId(item.id)}
                      className={`mb-0.5 w-full rounded-md px-2 py-1 text-left text-xs ${
                        item.id === libraryId ? "bg-[var(--color-muted)]" : "hover:bg-[var(--color-muted)]"
                      }`}
                    >
                      <div className="truncate">{item.name}</div>
                      <div className="text-[12px] text-[var(--color-muted-foreground)]">
                        {item.calc_method === "percent_of_slots"
                          ? "按百分比"
                          : `${formatMoney(item.unit_price_cents, "CNY")}/${item.unit}`}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
        </div>
      </aside>

      {/* 第 4 栏：组件详情 */}
      <aside className="flex w-80 shrink-0 flex-col border-l border-[var(--color-border)] bg-white">
        {rulerSelected && ruler ? (
          // 标尺属性：和组件一样是个可操作对象，可以改实际长度、拖动、旋转、拉伸
          <div className="flex flex-col gap-3 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">标尺</span>
              <Badge tone="muted">第 {pageIndex + 1} 页</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-muted-foreground)]">实际长度</span>
              <Input
                aria-label="标尺实际长度"
                type={inputUnit === "in" ? "text" : "number"}
                inputMode="decimal"
                className="h-8 w-28"
                autoFocus={rulerJustCreated}
                key={`ruler-panel-${pageIndex}-${ruler.lengthMm}-${inputUnit}`}
                defaultValue={formatLength(ruler.lengthMm, inputUnit)}
                onFocus={(event) => event.currentTarget.select()}
                onBlur={(event) => {
                  if (!drawingId) return;
                  const parsed = parseLength(event.target.value, inputUnit);
                  if (!parsed.ok) {
                    setError(parsed.error);
                    return;
                  }
                  const lengthMm = roundToMm(parsed.mm);
                  void api.saveRuler(drawingId, pageIndex, { ...ruler, lengthMm }).then(async () => {
                    const refreshed = await api.drawing(drawingId);
                    setPages(refreshed.pages);
                    setRulerJustCreated(false);
                    setError(null);
                    setStatus(`标尺已改为 ${Math.round(lengthMm)}mm`);
                  });
                }}
              />
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {LENGTH_UNIT_META[inputUnit].label}
              </span>
            </div>
            {inputUnit === "mm" ? null : (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                换算：{formatLength(ruler.lengthMm, inputUnit)} {LENGTH_UNIT_META[inputUnit].label} ={" "}
                <span className="tabular-nums">{Math.round(ruler.lengthMm)} mm</span>（
                <span className="tabular-nums">{formatFeetInches(ruler.lengthMm)}</span>）
              </p>
            )}
            <Separator />
            <div className="flex justify-between text-xs">
              <span className="text-[var(--color-muted-foreground)]">图上长度</span>
              <span className="tabular-nums">{rulerLengthOnPage?.toFixed(1) ?? "—"} pt</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[var(--color-muted-foreground)]">比例</span>
              <span className="tabular-nums">
                {mmPerPageUnit ? `${mmPerPageUnit.toFixed(3)} mm/pt` : "—"}
              </span>
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              拖动线身挪位置，拖两端圆点改长度/角度，按 <kbd>[</kbd> <kbd>]</kbd>{" "}
              旋转。画框时会按上面的比例自动算出宽高 ——标尺本身不参与报价，也不和其它柜体绑定。
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!drawingId) return;
                void api.clearRuler(drawingId, pageIndex).then(async () => {
                  const refreshed = await api.drawing(drawingId);
                  setPages(refreshed.pages);
                  setRulerSelected(false);
                  setStatus("标尺已删除");
                });
              }}
            >
              删除标尺
            </Button>
          </div>
        ) : libraryItem ? (
          <div className="flex flex-col gap-3 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{libraryItem.name}</span>
              <Badge tone="muted">{libraryItem.category_name}</Badge>
            </div>
            <div className="font-mono text-xs text-[var(--color-muted-foreground)]">{libraryItem.code}</div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-[var(--color-muted-foreground)]">计价方式</span>
              <span>{libraryItem.calc_method}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--color-muted-foreground)]">参数</span>
              <span className="font-mono text-xs">{libraryItem.params}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--color-muted-foreground)]">单价</span>
              <span className="tabular-nums">
                {libraryItem.calc_method === "percent_of_slots"
                  ? "按柜体+门板百分比"
                  : `${formatMoney(libraryItem.unit_price_cents, "CNY")} / ${libraryItem.unit}`}
              </span>
            </div>
            <Separator />
            {/* 这里只做"比价/看参数"。添加动作统一在柜体面板里完成，避免两处入口打架。 */}
            <p className="text-xs text-[var(--color-muted-foreground)]">
              这是目录里的价格参考。要把它用在某个柜体上，回到柜体面板用「添加组件」——
              {SINGLE_VALUE_SLOTS.has(slotForCategory(libraryItem.category_code))
                ? "柜体/门板/五金是单值的，选中即替换原来那行。"
                : "台面/见光板/配件可以叠加多行。"}
            </p>
            <Button
              variant="outline"
              disabled={!selected}
              onClick={() => {
                // 跳到柜体面板的同类选择器，并把搜索词预填成这个组件名
                setPickerCategory(libraryItem.category_code.split(".")[0] ?? "accessory");
                setPickerQuery(libraryItem.name.split(" ")[0] ?? libraryItem.name);
                setLibraryId(null);
              }}
            >
              {selected ? `回到 ${selected.label} 的组件面板` : "先选中一个柜体"}
            </Button>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              改价请到「组件目录」页面；已生成的报价保留快照，不受后续改价影响。
            </p>
          </div>
        ) : !selected ? (
          // 没选中条目时显示"报价设置"：客户币种 / 汇率 / 加价系数 / 整单调整
          <div className="flex flex-col gap-3 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">报价设置</span>
              <Badge tone="muted">整单生效</Badge>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted-foreground)]">客户币种</span>
              <Select
                aria-label="客户币种"
                value={settingsDraft.currency}
                onChange={(event) => {
                  const currency = event.target.value;
                  const preset = CURRENCY_PRESETS.find((entry) => entry.code === currency);
                  setSettingsDraft((current) => ({
                    ...current,
                    currency,
                    // 换币种时带出该币种的默认汇率，仍可手改
                    exchangeRate: preset ? String(preset.rate) : current.exchangeRate,
                  }));
                }}
              >
                {CURRENCY_PRESETS.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  汇率（1 {settingsDraft.currency} = ? 成本币种）
                </span>
                <Input
                  aria-label="汇率"
                  type="number"
                  step="0.01"
                  value={settingsDraft.exchangeRate}
                  onChange={(event) =>
                    setSettingsDraft({ ...settingsDraft, exchangeRate: event.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted-foreground)]">加价系数</span>
                <Input
                  aria-label="加价系数"
                  type="number"
                  step="0.01"
                  value={settingsDraft.markupFactor}
                  onChange={(event) =>
                    setSettingsDraft({ ...settingsDraft, markupFactor: event.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted-foreground)]">整单调整（%）</span>
              <Input
                aria-label="整单调整"
                type="number"
                step="0.1"
                value={settingsDraft.globalAdjustment}
                onChange={(event) =>
                  setSettingsDraft({ ...settingsDraft, globalAdjustment: event.target.value })
                }
              />
            </div>
            {/* 平面图开关：效果图/渲染图放进报价单不好看时，可以直接关掉 */}
            <label className="flex cursor-pointer items-start gap-2 rounded-md bg-[var(--color-muted)] p-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={settingsDraft.includeFloorPlan}
                onChange={(event) =>
                  setSettingsDraft({ ...settingsDraft, includeFloorPlan: event.target.checked })
                }
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-xs font-medium">报价单里附平面图</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  取消勾选后，客户版和工厂版都不出图纸页，只出报价明细
                </span>
              </span>
            </label>
            {/* 付款方式：报价单上照这段条款印，外贸 50/50、内销 5000 元定金 + 图纸确认后 70% */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted-foreground)]">付款方式</span>
              <Select
                aria-label="付款方式"
                value={settingsDraft.paymentTerms}
                onChange={(event) => setSettingsDraft({ ...settingsDraft, paymentTerms: event.target.value })}
              >
                <option value="deposit_50_50">50% 定金 / 余款 50% 装柜前付清（外贸）</option>
                <option value="deposit_5000_70">
                  定金 5000 元出图 / 图纸确认后 70% / 出货前付清（内销）
                </option>
              </Select>
            </div>
            {/* 整装出货：开了就在报价单上按货值 7% 单列一行打包组装费 */}
            <label className="flex cursor-pointer items-start gap-2 rounded-md bg-[var(--color-muted)] p-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={settingsDraft.assembledShipment}
                onChange={(event) =>
                  setSettingsDraft({ ...settingsDraft, assembledShipment: event.target.checked })
                }
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-xs font-medium">整装出货（按货值加收 7%）</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  勾上后报价单上单列一行「打包组装费」，不折进条目价格
                </span>
              </span>
            </label>
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(
                  () =>
                    api.updatePlan(plan.id, {
                      currency: settingsDraft.currency,
                      exchange_rate: Number(settingsDraft.exchangeRate),
                      markup_factor: Number(settingsDraft.markupFactor),
                      global_adjustment_percent: Number(settingsDraft.globalAdjustment),
                      include_floor_plan: settingsDraft.includeFloorPlan,
                      payment_terms: settingsDraft.paymentTerms,
                      assembled_shipment: settingsDraft.assembledShipment,
                    }),
                  `已按 ${settingsDraft.currency} 重算整单`,
                )
              }
            >
              保存并重算整单
            </Button>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              客户版按上面的币种出价（人民币 / 澳元 / 美元 / 英镑 / 欧元）。汇率含义：1
              单位客户币种折合多少成本币种； 改动只影响换算，不改任何目录单价。
            </p>
            <Separator />
            <p className="text-xs text-[var(--color-muted-foreground)]">
              在画布上画框或选中一个柜体，这里会切换成它的尺寸与计价明细。
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2">
              <span className="text-sm font-medium">
                {selected.label} · {selected.room ? `${selected.room} · ` : ""}
                {cabinetTypeName(selected.cabinet_type_code)}
              </span>
              <Badge tone={selected.size_status === "confirmed" ? "ok" : "warn"}>
                {selected.size_status === "confirmed" ? "尺寸已填" : "未填尺寸"}
              </Badge>
            </div>
            <div className="flex flex-col gap-3 overflow-auto p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[var(--color-muted-foreground)]">房间</span>
                  <div className="flex gap-1">
                    <Input
                      aria-label="房间"
                      list="plan-room-options"
                      placeholder="如 中厨 / Main Kitchen"
                      // 受控 + 按条目重置（key 跟着 dimsForItemId 变，切条目时整块重建）
                      key={`room-${dimsForItemId}`}
                      value={roomDraft}
                      onChange={(event) => setRoomDraft(event.target.value)}
                      onBlur={(event) => void commitRoom(selected.id, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      title="把上面的房间名套用到本页所有柜体"
                      onClick={() => void applyRoomToPage()}
                    >
                      本页
                    </Button>
                  </div>
                  <datalist id="plan-room-options">
                    {roomSuggestions.map((option) => (
                      <option key={option.value} value={option.value} label={option.label} />
                    ))}
                  </datalist>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[var(--color-muted-foreground)]">柜型</span>
                  <Select
                    aria-label="柜型"
                    value={selected.cabinet_type_code}
                    onChange={(event) => {
                      const nextType = event.target.value;
                      const previousDefault = defaultHeightFor(selected.cabinet_type_code);
                      const nextDefault = defaultHeightFor(nextType);
                      const previousDepthDefault = defaultDepthFor(selected.cabinet_type_code);
                      const nextDepthDefault = defaultDepthFor(nextType);
                      // 换柜型时"高/深"只在用户没改过时跟成新默认值（手填过的不覆盖）
                      const shouldFollowHeight = shouldFollowTypeDefault(
                        selected.height_mm,
                        previousDefault,
                        nextDefault,
                      );
                      const shouldFollowDepth = shouldFollowTypeDefault(
                        selected.depth_mm,
                        previousDepthDefault,
                        nextDepthDefault,
                      );
                      void patchSelected({
                        cabinet_type_code: nextType,
                        ...(shouldFollowHeight ? { height_mm: nextDefault } : {}),
                        ...(shouldFollowDepth ? { depth_mm: nextDepthDefault } : {}),
                      });
                    }}
                  >
                    {cabinetTypes.map((type) => (
                      <option key={type.code} value={type.code}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {/* 顺序按"平面图上看得到的两条边"排：长 → 深 → 高（高在平面图里量不出来，只能手填） */}
                {(["width_mm", "depth_mm", "height_mm"] as const).map((field) => (
                  <div key={field} className="flex flex-col gap-1">
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {field === "width_mm" ? widthLabel : field === "depth_mm" ? "深" : "高"}
                    </span>
                    <Input
                      aria-label={field}
                      // 英寸/英尺英寸要能写 24 1/2、10'6" —— number 类型的输入框不接受这些字符
                      type={inputUnit === "in" ? "text" : "number"}
                      inputMode="decimal"
                      placeholder={
                        field === "height_mm"
                          ? (() => {
                              const fallback = defaultHeightFor(selected.cabinet_type_code);
                              return fallback === null ? "" : formatLength(fallback, inputUnit);
                            })()
                          : undefined
                      }
                      // 受控 + 按条目重置：切柜体时显示的一定是"这个柜体"自己的尺寸
                      key={`${dimsForItemId}-${field}`}
                      autoFocus={field === "height_mm" && focusHeightFor === selected.id}
                      onFocus={(event) => {
                        if (field === "height_mm" && focusHeightFor === selected.id) {
                          event.currentTarget.select();
                        }
                      }}
                      value={dimsDraft[field] ?? ""}
                      onChange={(event) =>
                        setDimsDraft((current) => ({ ...current, [field]: event.target.value }))
                      }
                      onBlur={(event) => {
                        const raw = event.target.value.trim();
                        const current = selected[field] ?? null;
                        if (field === "height_mm") setFocusHeightFor(null);
                        if (raw === "") {
                          if (current !== null) void patchSelected({ [field]: null });
                          return;
                        }
                        // 只聚焦又原样离开：英寸按 1/16 显示会损失不到 1mm，别因此把值写回去
                        if (current !== null && raw === formatLength(current, inputUnit)) return;
                        // 英寸 / 厘米 / 米 → 毫米；填错就退回原值并提示，不把脏数据写进去
                        const parsed = parseLength(raw, inputUnit);
                        if (!parsed.ok) {
                          setDimsDraft((draft) => ({
                            ...draft,
                            [field]: current === null ? "" : formatLength(current, inputUnit),
                          }));
                          setError(parsed.error);
                          return;
                        }
                        const mm = roundToMm(parsed.mm);
                        if (mm === current) return;
                        void patchSelected({ [field]: mm });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* 非公制单位时把换算结果直接写出来：输入的是英寸，进库的是毫米，两边都要看得到 */}
              {inputUnit === "mm" ? null : (
                <p className="text-[11px] text-[var(--color-muted-foreground)]">
                  {(["width_mm", "depth_mm", "height_mm"] as const)
                    .map((field) => {
                      const raw = (dimsDraft[field] ?? "").trim();
                      if (!raw) return null;
                      const stored = selected[field] ?? null;
                      const parsed = parseLength(raw, inputUnit);
                      const name = field === "width_mm" ? widthLabel : field === "depth_mm" ? "深" : "高";
                      // 没改过的值以库里的毫米为准：英寸按 1/16 显示，换算回来会差不到 1mm
                      const mm =
                        stored !== null && raw === formatLength(stored, inputUnit)
                          ? stored
                          : parsed.ok
                            ? roundToMm(parsed.mm)
                            : null;
                      return `${name} ${raw} → ${mm === null ? "填法看不懂" : `${mm} mm`}`;
                    })
                    .filter((line): line is string => line !== null)
                    .join(" · ") ||
                    `按${LENGTH_UNIT_META[inputUnit].name}输入（${LENGTH_UNIT_META[inputUnit].hint}），落库自动换算成毫米`}
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                {(["doors", "drawers", "shelves"] as const).map((field) => (
                  <div key={field} className="flex flex-col gap-1">
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {field === "doors" ? "门数" : field === "drawers" ? "抽屉" : "层板"}
                    </span>
                    <Input
                      aria-label={field}
                      type="number"
                      key={`${dimsForItemId}-${field}`}
                      value={dimsDraft[field] ?? ""}
                      onChange={(event) =>
                        setDimsDraft((current) => ({ ...current, [field]: event.target.value }))
                      }
                      onBlur={(event) => {
                        const value = Number(event.target.value);
                        if (!Number.isFinite(value) || value === selected[field]) return;
                        void patchSelected({ [field]: value });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                      }}
                    />
                  </div>
                ))}
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="font-medium">计价明细</span>
                <span className="tabular-nums">
                  {formatMoney(selected.cost_cents, "CNY")} →{" "}
                  {formatMoney(selected.price_cents, plan.currency)}
                </span>
              </div>
              {components.some(
                (component) =>
                  component.calc_method === "area_factor" &&
                  (component.slot === "countertop" || component.slot === "panel"),
              ) ? (
                <p className="text-[12px] text-[var(--color-muted-foreground)]">
                  台面 / 见光板的「面积」可以自己填：台面留空按<strong>长×深</strong>
                  算，见光板必须填（看得到几面就填多大）。
                </p>
              ) : null}
              <Table>
                <thead>
                  <tr>
                    <Th>组件</Th>
                    <Th className="text-right">数量</Th>
                    <Th className="text-right">金额</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {components.map((component) => (
                    <tr key={component.id}>
                      <Td>
                        <div className="flex flex-col">
                          <span className="text-xs">{component.name_snapshot}</span>
                          <span className="font-mono text-[12px] text-[var(--color-muted-foreground)]">
                            {component.slot} · {component.calc_method}
                            {component.calc_method === "area_factor"
                              ? ` · ${areaBasisLabel(componentParams(component))}`
                              : ""}
                          </span>
                          {component.state === "ok" ? null : (
                            <span className="text-[12px] text-amber-700">{component.notes ?? "待配置"}</span>
                          )}
                        </div>
                      </Td>
                      <Td className="text-right tabular-nums">
                        {component.calc_method === "area_factor" &&
                        (component.slot === "countertop" ||
                          component.slot === "panel" ||
                          componentParams(component).requiresManualArea === true ||
                          typeof componentParams(component).manualAreaM2 === "number") ? (
                          // 见光板必须填面积；台面可以不填（留空 = 长×深），填了就用你填的
                          <Input
                            aria-label={`面积 ${component.catalog_code}`}
                            type="number"
                            step="0.01"
                            min={0}
                            placeholder={component.slot === "panel" ? "填面积" : "长×深"}
                            className="h-7 w-20 text-right"
                            key={`${component.id}-${String(componentParams(component).manualAreaM2 ?? "empty")}`}
                            defaultValue={
                              typeof componentParams(component).manualAreaM2 === "number"
                                ? String(componentParams(component).manualAreaM2)
                                : ""
                            }
                            onBlur={(event) => {
                              const raw = event.target.value.trim();
                              const value = raw === "" ? undefined : Number(raw);
                              if (value !== undefined && (!Number.isFinite(value) || value <= 0)) return;
                              const params = componentParams(component);
                              if (value === undefined && params.manualAreaM2 === undefined) return;
                              const next = { ...params };
                              if (value === undefined) delete next.manualAreaM2;
                              else next.manualAreaM2 = value;
                              void run(() =>
                                api
                                  .updateComponent(selected.id, component.id, { params: next })
                                  .then(() => api.recompute(selected.id)),
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") event.currentTarget.blur();
                            }}
                          />
                        ) : component.calc_method === "count_price" ? (
                          // 按件组件（抽屉、拉篮、水槽、配件…）的数量可以逐行改：
                          // 写进这一行的 params.fixedCount，只影响这一行
                          <Input
                            aria-label={`数量 ${component.catalog_code}`}
                            type="number"
                            min={0}
                            className="h-7 w-16 text-right"
                            key={`${component.id}-${component.quantity}`}
                            defaultValue={component.quantity}
                            onBlur={(event) => {
                              const value = Number(event.target.value);
                              if (!Number.isFinite(value) || value < 0 || value === component.quantity)
                                return;
                              let params: Record<string, unknown> = {};
                              try {
                                params = JSON.parse(component.params);
                              } catch {
                                params = {};
                              }
                              void run(() =>
                                api
                                  .updateComponent(selected.id, component.id, {
                                    params: { ...params, fixedCount: value },
                                  })
                                  .then(() => api.recompute(selected.id)),
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") event.currentTarget.blur();
                            }}
                          />
                        ) : (
                          component.quantity.toFixed(3)
                        )}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {formatMoney(component.amount_cents, "CNY")}
                      </Td>
                      <Td className="text-right">
                        <button
                          type="button"
                          className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]"
                          onClick={() =>
                            void run(() =>
                              api
                                .deleteComponent(selected.id, component.id)
                                .then(() => api.recompute(selected.id)),
                            )
                          }
                        >
                          删除
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {components.some((component) => component.state !== "ok") ? (
                <p className="text-xs text-amber-700">
                  有「待配置」行（缺尺寸或缺少依赖），金额为 0 且不计入合计。
                </p>
              ) : null}

              {/* 添加组件：不用切到别的栏，就在这里按分类挑；加完立刻出现在上面的明细里 */}
              <Separator />
              <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">
                    {SINGLE_VALUE_SLOTS.has(slotForCategory(pickerCategory)) ? "更换组件" : "添加组件"}
                  </span>
                  <span className="text-[12px] text-[var(--color-muted-foreground)]">
                    加到 {selected.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(CATEGORY_LABEL).map(([code, label]) => (
                    <button
                      type="button"
                      key={code}
                      onClick={() => setPickerCategory(code)}
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        pickerCategory === code
                          ? "bg-[var(--color-primary)] text-white"
                          : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[12px] text-[var(--color-muted-foreground)]">
                  {SINGLE_VALUE_SLOTS.has(slotForCategory(pickerCategory))
                    ? "柜体 / 门板 / 五金是单值的：点一款就替换现在那一行（不会重复计价）"
                    : "台面 / 见光板 / 配件可以叠加多行"}
                </p>
                <Input
                  aria-label="搜索组件"
                  placeholder="搜索名称或代码"
                  value={pickerQuery}
                  onChange={(event) => setPickerQuery(event.target.value)}
                />
                <div className="max-h-52 overflow-auto">
                  {catalog
                    .filter((item) => (item.category_code.split(".")[0] ?? "") === pickerCategory)
                    .filter((item) =>
                      pickerQuery.trim() === ""
                        ? true
                        : `${item.name}${item.code}`.toLowerCase().includes(pickerQuery.trim().toLowerCase()),
                    )
                    .map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        disabled={busy}
                        onClick={() =>
                          void (async () => {
                            setBusy(true);
                            try {
                              const slot = slotForCategory(item.category_code);
                              const existing = components.find((component) => component.slot === slot);
                              let feedback = `已把「${item.name}」加到 ${selected.label}`;
                              if (existing && SINGLE_VALUE_SLOTS.has(slot)) {
                                // 单值槽位 = 换材料：直接替换那一行，避免重复计价
                                if (existing.calc_method === item.calc_method) {
                                  /*
                                   * 口径（系数/百分比/件数来源）用新组件的，但手填的量要跟着旧行走：
                                   * 换抽屉材质不能把「6 个」变回 1 个，换见光板不能丢掉手量面积。
                                   */
                                  await api.updateComponent(selected.id, existing.id, {
                                    catalog_item_id: item.id,
                                    params: mergeReplaceParams(
                                      JSON.parse(existing.params) as Record<string, unknown>,
                                      JSON.parse(item.params) as Record<string, unknown>,
                                    ),
                                  });
                                } else {
                                  await api.deleteComponent(selected.id, existing.id);
                                  await api.addComponent(selected.id, { slot, catalog_item_id: item.id });
                                }
                                feedback = `${selected.label} 的${
                                  CATEGORY_LABEL[item.category_code.split(".")[0] ?? ""] ?? "组件"
                                }已换成「${item.name}」`;
                              } else {
                                await api.addComponent(selected.id, { slot, catalog_item_id: item.id });
                              }
                              const computed = await api.recompute(selected.id);
                              applyComputation(computed);
                              setStatus(feedback);
                              setError(null);
                            } catch (caught) {
                              setError(caught instanceof Error ? caught.message : "添加组件失败");
                            } finally {
                              setBusy(false);
                            }
                          })()
                        }
                        className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs hover:bg-[var(--color-muted)] disabled:opacity-50"
                      >
                        <span className="truncate">
                          {components.some((component) => component.catalog_code === item.code) ? "✓ " : ""}
                          {item.name}
                        </span>
                        <span className="ml-2 shrink-0 tabular-nums text-[var(--color-muted-foreground)]">
                          {item.calc_method === "percent_of_slots"
                            ? "按百分比"
                            : `${formatMoney(item.unit_price_cents, "CNY")}/${item.unit}`}
                        </span>
                      </button>
                    ))}
                  {catalog.filter((item) => (item.category_code.split(".")[0] ?? "") === pickerCategory)
                    .length === 0 ? (
                    <p className="px-2 py-2 text-xs text-[var(--color-muted-foreground)]">
                      这个分类下暂时没有组件
                    </p>
                  ) : null}
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => void run(() => api.deleteItem(selected.id)).then(() => setSelectedId(null))}
              >
                删除这个柜体
              </Button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
